<template>
  <div class="settings-page">
    <!-- 左侧导航栏 -->
    <aside class="settings-sidebar">
      <nav class="sidebar-nav">
        <div
          v-for="(item, index) in sidebarMenu"
          :key="index"
          class="nav-item"
          :class="{ active: activeSection === item.id }"
          @click="scrollToSection(item.id)"
        >
          <img :src="item.image" alt="" class="nav-icon" />
          <span>{{ item.name }}</span>
        </div>
      </nav>
    </aside>

    <!-- 右侧内容区 -->
    <main class="settings-main">
      <!-- 账号管理 -->
      <section id="account" class="settings-section" ref="account">
      <h3 class="section-title">账号管理</h3>
      <div class="settings-list">
        <!-- 编辑资料 -->
        <div class="setting-item" @click="goToEditProfile">
          <div class="item-left">
            <span class="item-title">编辑资料</span>
            <span class="item-desc">修改您的基本信息</span>
          </div>
          <div class="item-right">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
        <!-- 账户安全 -->
        <div class="setting-item" @click="goToSecurity">
          <div class="item-left">
            <span class="item-title">账户安全</span>
            <span class="item-desc">修改密码</span>
          </div>
          <div class="item-right">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
      </div>
    </section>

    <!-- 推送通知 -->
    <section id="notification" class="settings-section" ref="notification">
      <h3 class="section-title">推送通知</h3>
      <div class="settings-list">
        <!-- 桌面通知 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">桌面通知</span>
            <span class="item-desc">允许在桌面推送互动、新视频等内容的通知，您随时可以关闭</span>
          </div>
          <div class="item-right">
            <div class="custom-switch" :class="{ active: settings.desktopNotify }" @click.stop="settings.desktopNotify = !settings.desktopNotify">
              <div class="switch-track">
                <div class="switch-thumb"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- 互动通知 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">互动通知</span>
            <span class="item-desc">点赞、评论、粉丝和关注等</span>
          </div>
          <div class="item-right">
            <div class="custom-switch" :class="{ active: settings.interactionNotify }" @click.stop="settings.interactionNotify = !settings.interactionNotify">
              <div class="switch-track">
                <div class="switch-thumb"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 隐私 -->
    <section id="privacy" class="settings-section" ref="privacy">
      <h3 class="section-title">隐私</h3>
      <div class="settings-list">
        <!-- 账号可见性 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">账号可见性</span>
            <span class="item-desc">私密账号开关，仅与您互相关注的好友可见</span>
          </div>
          <div class="item-right">
            <span class="item-value">{{ settings.accountPrivate ? '私密' : '公开' }}</span>
            <div class="custom-switch" :class="{ active: settings.accountPrivate }" @click.stop="settings.accountPrivate = !settings.accountPrivate">
              <div class="switch-track">
                <div class="switch-thumb"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- 内容可见性 -->
        <div class="setting-item" @click="goToContentVisibility">
          <div class="item-left">
            <span class="item-title">内容可见性</span>
            <span class="item-desc">您的观看历史、收藏、已赞内容是否公开</span>
          </div>
          <div class="item-right">
            <span class="item-value">{{ settings.contentVisibility }}</span>
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
        <!-- 互动 - 评论 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">评论</span>
            <span class="item-desc">谁可以对你的作品发表评论</span>
          </div>
          <div class="item-right">
            <span class="item-value">{{ settings.commentPermission }}</span>
            <div class="custom-switch" :class="{ active: settings.commentPermission === '所有人' }" @click.stop="toggleCommentPermission">
              <div class="switch-track">
                <div class="switch-thumb"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- 互动 - 私信 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">私信</span>
            <span class="item-desc">谁可以发消息给你</span>
          </div>
          <div class="item-right">
            <span class="item-value">{{ settings.messagePermission }}</span>
            <div class="custom-switch" :class="{ active: settings.messagePermission === '所有人' }" @click.stop="toggleMessagePermission">
              <div class="switch-track">
                <div class="switch-thumb"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- 隐私协议 -->
        <div class="setting-item" @click="goToPrivacyPolicy">
          <div class="item-left">
            <span class="item-title">隐私协议</span>
          </div>
          <div class="item-right">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
      </div>
    </section>

    <!-- 钱包 -->
    <section id="wallet" class="settings-section" ref="wallet">
      <h3 class="section-title">钱包</h3>
      <div class="settings-list">
        <!-- 余额及充值 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">余额及充值</span>
            <span class="item-balance">{{ wallet.balance }}</span>
          </div>
          <div class="item-right">
            <button class="recharge-btn" @click="recharge">充值</button>
          </div>
        </div>
        <!-- 兑换礼物 -->
        <div class="setting-item" @click="goToExchangeGifts">
          <div class="item-left">
            <span class="item-title">兑换礼物</span>
          </div>
          <div class="item-right">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
      </div>
    </section>

    <!-- 意见反馈与帮助 -->
    <section id="feedback" class="settings-section" ref="feedback">
      <h3 class="section-title">意见反馈与帮助</h3>
      <div class="settings-list">
        <!-- 意见反馈 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">意见反馈</span>
            <div class="item-contact">
              <a :href="'mailto:' + contact.feedbackEmail" class="item-link">{{ contact.feedbackEmail }}</a>
              <span class="copy-btn" @click="copyEmail(contact.feedbackEmail)">复制</span>
            </div>
          </div>
        </div>
        <!-- 帮助 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">帮助</span>
            <div class="item-contact">
              <a :href="'mailto:' + contact.helpEmail" class="item-link">{{ contact.helpEmail }}</a>
              <span class="copy-btn" @click="copyEmail(contact.helpEmail)">复制</span>
            </div>
          </div>
        </div>
        <!-- 用户协议 -->
        <div class="setting-item" @click="goToUserAgreement">
          <div class="item-left">
            <span class="item-title">用户协议</span>
          </div>
          <div class="item-right">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
      </div>
    </section>
    </main>

    <!-- 编辑资料弹窗 -->
    <div v-if="showEditModal" class="modal-overlay" @click.self="closeEditModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>编辑资料</h3>
          <i class="el-icon-close" @click="closeEditModal"></i>
        </div>
        <div class="modal-body">
          <!-- 用户昵称 -->
          <div class="form-item">
            <label>用户昵称</label>
            <input v-model="editForm.nickname" type="text" placeholder="请输入昵称" />
          </div>
          <!-- 账号信息 -->
          <div class="form-item">
            <label>账号信息</label>
            <div class="account-info">
              <div class="account-left">
                <span class="account-text">账号：{{ editForm.email }}</span>
              </div>
              <div class="account-right">
                <span class="account-id">ID：{{ editForm.userId }}</span>
                <span class="copy-btn" @click="copyId(editForm.userId)">复制</span>
              </div>
            </div>
          </div>
          <!-- 性别 -->
          <div class="form-item">
            <label>性别</label>
            <div class="gender-options">
              <label class="radio-label">
                <input v-model="editForm.gender" type="radio" value="male" />
                <span class="radio-custom"></span>
                <span>男</span>
              </label>
              <label class="radio-label">
                <input v-model="editForm.gender" type="radio" value="female" />
                <span class="radio-custom"></span>
                <span>女</span>
              </label>
            </div>
          </div>
          <!-- 生日 -->
          <div class="form-item">
            <label>生日</label>
            <div class="date-input">
              <input v-model="editForm.birthday" type="date" />
              <i class="el-icon-date"></i>
            </div>
          </div>
          <!-- 自我介绍 -->
          <div class="form-item">
            <label>自我介绍</label>
            <textarea v-model="editForm.bio" rows="4" placeholder="请输入自我介绍"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-primary" @click="saveProfile">保存</button>
          <button class="btn-default" @click="closeEditModal">取消</button>
        </div>
      </div>
    </div>

    <!-- 账户安全弹窗 -->
    <div v-if="showSecurityModal" class="modal-overlay" @click.self="closeSecurityModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>账户安全</h3>
          <i class="el-icon-close" @click="closeSecurityModal"></i>
        </div>
        <div class="modal-body">
          <!-- 原密码 -->
          <div class="form-item">
            <label>原密码</label>
            <input v-model="securityForm.oldPassword" type="password" placeholder="请输入原密码" />
          </div>
          <!-- 新密码 -->
          <div class="form-item">
            <label>新密码</label>
            <input v-model="securityForm.newPassword" type="password" placeholder="请输入新密码" />
          </div>
          <!-- 确认密码 -->
          <div class="form-item">
            <label>确认密码</label>
            <input v-model="securityForm.confirmPassword" type="password" placeholder="请再次输入新密码" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-primary" @click="savePassword">保存</button>
          <button class="btn-default" @click="closeSecurityModal">取消</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SettingsPage',
  data() {
    return {
      // 当前激活的区块
      activeSection: 'account',
      // 侧边栏菜单
      sidebarMenu: [
        { id: 'account', name: '账号管理', image: require('../../assets/zh2.png') },
        { id: 'notification', name: '推送通知', image: require('../../assets/tz2.png') },
        { id: 'privacy', name: '隐私', image: require('../../assets/ys2.png') },
        { id: 'wallet', name: '钱包', image: require('../../assets/xb2.png') },
        { id: 'feedback', name: '意见反馈与帮助', image: require('../../assets/yh2.png') }
      ],
      // 弹窗显示状态
      showEditModal: false,
      showSecurityModal: false,
      // 编辑资料表单
      editForm: {
        nickname: '魔法少女慈禧',
        email: '123@qq.com',
        userId: '1234567',
        gender: 'male',
        birthday: '',
        bio: ''
      },
      // 修改密码表单
      securityForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      // 设置项数据
      settings: {
        // 桌面通知开关
        desktopNotify: true,
        // 互动通知开关
        interactionNotify: true,
        // 账号是否私密
        accountPrivate: true,
        // 内容可见性
        contentVisibility: '私密',
        // 评论权限
        commentPermission: '所有人',
        // 私信权限
        messagePermission: '所有人'
      },
      // 钱包信息
      wallet: {
        balance: 20
      },
      // 联系邮箱
      contact: {
        feedbackEmail: '12356789999@xx.com',
        helpEmail: '12356789999@xx.com'
      }
    }
  },
  mounted() {
    // 监听滚动，更新当前激活的区块
    this.initScrollListener()
  },
  beforeDestroy() {
    // 移除滚动监听
    this.removeScrollListener()
  },
  methods: {
    // 初始化滚动监听
    initScrollListener() {
      const main = document.querySelector('.settings-main')
      if (main) {
        main.addEventListener('scroll', this.handleScroll)
      }
    },
    // 移除滚动监听
    removeScrollListener() {
      const main = document.querySelector('.settings-main')
      if (main) {
        main.removeEventListener('scroll', this.handleScroll)
      }
    },
    // 处理滚动事件
    handleScroll() {
      const sections = ['account', 'notification', 'privacy', 'wallet', 'feedback']
      const main = document.querySelector('.settings-main')
      if (!main) return

      const scrollTop = main.scrollTop
      const offset = 100 // 偏移量

      for (const sectionId of sections) {
        const section = this.$refs[sectionId]
        if (section) {
          const sectionTop = section.offsetTop
          const sectionHeight = section.offsetHeight
          if (scrollTop >= sectionTop - offset && scrollTop < sectionTop + sectionHeight - offset) {
            this.activeSection = sectionId
            break
          }
        }
      }
    },
    // 滚动到指定区块
    scrollToSection(sectionId) {
      this.activeSection = sectionId
      const section = this.$refs[sectionId]
      if (section) {
        const main = document.querySelector('.settings-main')
        if (main) {
          main.scrollTo({
            top: section.offsetTop - 20,
            behavior: 'smooth'
          })
        }
      }
    },
    // 打开编辑资料弹窗
    goToEditProfile() {
      this.showEditModal = true
    },
    // 关闭编辑资料弹窗
    closeEditModal() {
      this.showEditModal = false
    },
    // 保存资料
    saveProfile() {
      this.$emit('saveProfile', this.editForm)
      this.showEditModal = false
    },
    // 复制ID
    copyId(id) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(id).then(() => {
          this.$message.success('已复制')
        })
      } else {
        const input = document.createElement('input')
        input.value = id
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        this.$message.success('已复制')
      }
    },
    // 打开账户安全弹窗
    goToSecurity() {
      this.showSecurityModal = true
    },
    // 关闭账户安全弹窗
    closeSecurityModal() {
      this.showSecurityModal = false
    },
    // 保存密码
    savePassword() {
      if (this.securityForm.newPassword !== this.securityForm.confirmPassword) {
        this.$message.error('两次输入的密码不一致')
        return
      }
      this.$emit('savePassword', this.securityForm)
      this.securityForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
      this.showSecurityModal = false
    },
    // 跳转到内容可见性设置
    goToContentVisibility() {
      this.$emit('goToContentVisibility')
    },
    // 跳转到评论设置
    goToCommentSettings() {
      this.$emit('goToCommentSettings')
    },
    // 跳转到私信设置
    goToMessageSettings() {
      this.$emit('goToMessageSettings')
    },
    // 跳转到隐私协议
    goToPrivacyPolicy() {
      this.$router.push('/yinsi')
    },
    // 充值
    recharge() {
      this.$emit('recharge')
    },
    // 跳转到兑换礼物
    goToExchangeGifts() {
      this.$emit('goToExchangeGifts')
    },
    // 复制邮箱
    copyEmail(email) {
      // 复制到剪贴板
      if (navigator.clipboard) {
        navigator.clipboard.writeText(email).then(() => {
          this.$message.success('已复制')
        })
      } else {
        // 兼容旧浏览器
        const input = document.createElement('input')
        input.value = email
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        this.$message.success('已复制')
      }
    },
    // 跳转到用户协议
    goToUserAgreement() {
      this.$router.push('/xieyi')
    },
    // 切换评论权限
    toggleCommentPermission() {
      this.settings.commentPermission = this.settings.commentPermission === '所有人' ? '仅互关' : '所有人'
    },
    // 切换私信权限
    toggleMessagePermission() {
      this.settings.messagePermission = this.settings.messagePermission === '所有人' ? '仅互关' : '所有人'
    }
  }
}
</script>

<style scoped lang="scss">
// 页面主容器
.settings-page {
  display: flex;
  min-height: 100vh;
  background: #f8f9fa;
}

// 左侧侧边栏
.settings-sidebar {
  width: 220px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  margin: 24px;
  padding: 16px 0;
  flex-shrink: 0;
  height: fit-content;

  // 侧边栏导航
  .sidebar-nav {
    display: flex;
    flex-direction: column;
  }

  // 导航项
  .nav-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 20px;
    margin: 0 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    color: #666;
    font-size: 14px;
    border-radius: 8px;

    .nav-icon {
      height: 15px;
    }

    &:hover {
      background: #f9f9f9;
      color: #8B5CF6;
    }

    // 激活状态
    &.active {
      background: #f3f0ff;
      color: #8B5CF6;

      .nav-icon {
        filter: brightness(0) saturate(100%) invert(45%) sepia(100%) saturate(500%) hue-rotate(250deg) brightness(95%) contrast(90%);
      }
    }
  }
}

// 右侧主内容区
.settings-main {
  flex: 1;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  margin: 24px 24px 24px 0;
  padding: 24px 32px;
  overflow-y: auto;
  height: calc(100vh - 48px);
}

// 设置区块
.settings-section {
  margin-bottom: 32px;

  // 区块标题
  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0 0 16px 0;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
  }
}

// 设置列表
.settings-list {
  display: flex;
  flex-direction: column;
}

// 设置项
.setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  cursor: pointer;
  transition: background 0.3s ease;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }

  // 左侧内容区
  .item-left {
    display: flex;
    flex-direction: column;
    gap: 4px;

    // 设置项标题
    .item-title {
      font-size: 14px;
      color: #333;
      font-weight: 500;
    }

    // 设置项描述
    .item-desc {
      font-size: 12px;
      color: #999;
    }

    // 余额显示
    .item-balance {
      font-size: 14px;
      color: #333;
      margin-top: 4px;
    }

    // 联系方式区域
    .item-contact {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 4px;

      // 邮箱链接
      .item-link {
        font-size: 13px;
        color: #8B5CF6;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }

      // 复制按钮
      .copy-btn {
        font-size: 12px;
        color: #8B5CF6;
        cursor: pointer;
        padding: 2px 8px;
        border: 1px solid #8B5CF6;
        border-radius: 4px;

        &:hover {
          background: #f3f0ff;
        }
      }
    }
  }

  // 右侧内容区
  .item-right {
    display: flex;
    align-items: center;
    gap: 12px;

    // 设置项值（如"私密"、"所有人"）
    .item-value {
      font-size: 13px;
      color: #666;

      // 主色调文字（紫色）
      &.text-primary {
        color: #8B5CF6;
      }
    }

    // 箭头图标
    .el-icon-arrow-right {
      font-size: 14px;
      color: #ccc;
    }

    // 自定义开关样式
    .custom-switch {
      cursor: pointer;

      .switch-track {
        width: 44px;
        height: 22px;
        background: #dcdfe6;
        border-radius: 11px;
        position: relative;
        transition: background 0.3s ease;

        .switch-thumb {
          width: 18px;
          height: 18px;
          background: #fff;
          border-radius: 50%;
          position: absolute;
          top: 2px;
          left: 2px;
          transition: transform 0.3s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      }

      // 激活状态
      &.active {
        .switch-track {
          background: #8B5CF6;

          .switch-thumb {
            transform: translateX(22px);
          }
        }
      }
    }

    // 充值按钮
    .recharge-btn {
      padding: 6px 20px;
      background: #8B5CF6;
      border: none;
      border-radius: 16px;
      color: #fff;
      font-size: 13px;
      cursor: pointer;
      transition: opacity 0.3s ease;

      &:hover {
        opacity: 0.9;
      }
    }
  }
}

// 弹窗遮罩层
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

// 弹窗内容
.modal-content {
  background: #fff;
  border-radius: 16px;
  width: 600px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

// 弹窗头部
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  border-bottom: 1px solid #f0f0f0;

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0;
  }

  .el-icon-close {
    font-size: 20px;
    color: #999;
    cursor: pointer;

    &:hover {
      color: #666;
    }
  }
}

// 弹窗主体
.modal-body {
  padding: 24px 32px;
}

// 表单项
.form-item {
  margin-bottom: 16px;

  label {
    display: block;
    font-size: 14px;
    color: #333;
    margin-bottom: 6px;
    font-weight: 500;
  }

  input[type="text"],
  input[type="password"],
  input[type="date"],
  textarea {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    color: #333;
    background: #f8f8f8;
    outline: none;
    transition: border-color 0.3s ease;
    box-sizing: border-box;

    &:focus {
      border-color: #8B5CF6;
    }

    &::placeholder {
      color: #999;
    }
  }

  textarea {
    resize: none;
    min-height: 60px;
  }
}

// 账号信息行
.account-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  font-size: 14px;
  color: #666;

  .account-left {
    display: flex;
    gap: 8px;
  }

  .account-text {
    color: #999;
  }

  .account-id {
    color: #666;
  }

  .copy-btn {
    color: #8B5CF6;
    cursor: pointer;
    font-size: 13px;

    &:hover {
      text-decoration: underline;
    }
  }
}

// 性别选项
.gender-options {
  display: flex;
  gap: 32px;

  .radio-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 15px;
    color: #333;

    input[type="radio"] {
      display: none;
    }

    .radio-custom {
      width: 18px;
      height: 18px;
      border: 2px solid #d0d0d0;
      border-radius: 50%;
      position: relative;
      transition: all 0.3s ease;

      &::after {
        content: '';
        width: 10px;
        height: 10px;
        background: #8B5CF6;
        border-radius: 50%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(0);
        transition: transform 0.3s ease;
      }
    }

    input[type="radio"]:checked + .radio-custom {
      border-color: #8B5CF6;

      &::after {
        transform: translate(-50%, -50%) scale(1);
      }
    }
  }
}

// 日期输入框
.date-input {
  position: relative;

  input[type="date"] {
    width: 100%;
    padding-right: 40px;
    box-sizing: border-box;
  }

  .el-icon-date {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    font-size: 16px;
    pointer-events: none;
  }
}

// 弹窗底部
.modal-footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 16px 32px 24px;

  .btn-primary {
    padding: 10px 32px;
    background: #8B5CF6;
    border: none;
    border-radius: 20px;
    color: #fff;
    font-size: 14px;
    cursor: pointer;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 0.9;
    }
  }

  .btn-default {
    padding: 10px 32px;
    background: #f0f0f0;
    border: none;
    border-radius: 20px;
    color: #666;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.3s ease;

    &:hover {
      background: #e0e0e0;
    }
  }
}
</style>